/**
 * Payment Provider Service
 * Off-platform Stripe/PayPal links (default) or Stripe Connect + approval (when enabled).
 */

import { Platform } from 'react-native';
import {
  STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL,
  STRIPE_CONNECT_FOR_DELIVERY_ENABLED,
} from '../constants/paymentProviderFlags';
import { getMerchantTermsForApi } from './merchantTermsService';
import {
  canConnectStripeFromApproval,
  fetchStripeConnectApproval,
} from './stripeConnectApprovalService';
import { AuthManager } from '../utils/authManager';
import { isStripeDirectPaymentUrl } from '../utils/stripeDirectPayment';

export interface PaymentProviderStatus {
  connected: boolean;
  ready: boolean;
  accountId?: string;
  error?: string;
}

export interface ConnectAccountResponse {
  success: boolean;
  connectUrl?: string;
  accountId?: string;
  error?: string;
}

// React Native: never use localhost (device = self). Use HTTPS (iOS ATS).
const PAYMENT_API_BASE = Platform.OS === 'web' && __DEV__
  ? 'http://localhost:3000/api'
  : 'https://bookapt-payment-api.onrender.com/api';

class PaymentProviderService {
  private readonly API_BASE_URL = PAYMENT_API_BASE;

  /**
   * Stripe: direct Payment Link (default) or Connect account when approval flow is enabled.
   */
  async checkStripeStatus(businessId: string): Promise<PaymentProviderStatus> {
    try {
      const { getSavedStripeDirectUrl } = await import('./stripeDirectPaymentService');
      const directUrl = await getSavedStripeDirectUrl(businessId);
      if (directUrl && isStripeDirectPaymentUrl(directUrl)) {
        return {
          connected: true,
          ready: true,
          accountId: directUrl,
        };
      }

      if (!STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL && !STRIPE_CONNECT_FOR_DELIVERY_ENABLED) {
        return { connected: false, ready: false };
      }

      let accountIdQuery = '';
      try {
        const { AuthManager } = await import('../utils/authManager');
        const owner = await AuthManager.getSavedBusinessOwner();
        const acct =
          owner?.id === businessId &&
          typeof owner.stripeConnectAccountId === 'string' &&
          owner.stripeConnectAccountId.startsWith('acct_')
            ? owner.stripeConnectAccountId
            : null;
        if (acct) {
          accountIdQuery = `?accountId=${encodeURIComponent(acct)}`;
        }
      } catch {
        // ignore — status still works from server in-memory map when present
      }

      const response = await fetch(
        `${this.API_BASE_URL}/stripe-connect/status/${businessId}${accountIdQuery}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Backfill: older installs never stored Connect account id locally; status response includes it.
      if (
        data.connected &&
        typeof data.accountId === 'string' &&
        data.accountId.startsWith('acct_') &&
        businessId
      ) {
        try {
          const { AuthManager } = await import('../utils/authManager');
          const owner = await AuthManager.getSavedBusinessOwner();
          if (owner?.id === businessId && !owner.stripeConnectAccountId) {
            await AuthManager.saveBusinessOwner({
              ...owner,
              stripeConnectAccountId: data.accountId,
            });
          }
        } catch {
          /* non-fatal */
        }
      }

      return {
        connected: data.connected || false,
        ready: data.ready || false,
        accountId: data.accountId,
        error: data.error,
      };
    } catch (error: any) {
      // Only log if it's not a connection refused error (backend not available)
      if (!error.message?.includes('Failed to fetch') && 
          !error.message?.includes('ERR_CONNECTION_REFUSED') && 
          !error.message?.includes('NetworkError')) {
        console.error('Error checking Stripe status:', error);
      }
      return {
        connected: false,
        ready: false,
        error: error.message || 'Failed to check Stripe status',
      };
    }
  }

  /**
   * Create Stripe Connect account link (OAuth flow)
   */
  async createStripeConnectAccount(
    businessId: string,
    businessName: string,
    returnUrl: string = 'bookapt://stripe-connect-success',
    refreshUrl: string = 'bookapt://stripe-connect-refresh'
  ): Promise<ConnectAccountResponse> {
    try {
      const owner = await AuthManager.getSavedBusinessOwner();
      const merchantTerms = getMerchantTermsForApi(owner);
      if (!merchantTerms) {
        return {
          success: false,
          error: 'MERCHANT_TERMS_REQUIRED',
        };
      }
      if (STRIPE_CONNECT_FOR_DELIVERY_ENABLED) {
        const approvalRes = await fetchStripeConnectApproval(businessId);
        const approvalRecord =
          approvalRes.approval || owner?.stripeConnectApproval || null;
        if (
          STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL &&
          !canConnectStripeFromApproval(approvalRecord)
        ) {
          return {
            success: false,
            error: 'STRIPE_APPROVAL_REQUIRED',
          };
        }
      } else if (STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL) {
        const approvalRes = await fetchStripeConnectApproval(businessId);
        const approvalRecord =
          approvalRes.approval || owner?.stripeConnectApproval || null;
        if (!canConnectStripeFromApproval(approvalRecord)) {
          return {
            success: false,
            error: 'STRIPE_APPROVAL_REQUIRED',
          };
        }
      }

      const response = await fetch(`${this.API_BASE_URL}/stripe-connect/create-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId,
          businessName,
          returnUrl,
          refreshUrl,
          merchantTerms,
        }),
      });

      const errorData = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (errorData.requiresApproval) {
          return {
            success: false,
            error: 'STRIPE_APPROVAL_REQUIRED',
          };
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = errorData;

      if (
        data.success &&
        typeof data.accountId === 'string' &&
        data.accountId.startsWith('acct_') &&
        businessId
      ) {
        try {
          const { AuthManager } = await import('../utils/authManager');
          const owner = await AuthManager.getSavedBusinessOwner();
          if (owner?.id === businessId) {
            await AuthManager.saveBusinessOwner({
              ...owner,
              stripeConnectAccountId: data.accountId,
            });
          }
        } catch (persistErr) {
          console.warn('paymentProviderService: could not persist stripeConnectAccountId', persistErr);
        }
      }

      return {
        success: data.success || false,
        connectUrl: data.connectUrl,
        accountId: data.accountId,
        error: data.error,
      };
    } catch (error: any) {
      console.error('Error creating Stripe Connect account:', error);
      
      // Check if it's a connection error (backend not available)
      if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_REFUSED') || error.message?.includes('NetworkError')) {
        return {
          success: false,
          error: 'BACKEND_NOT_AVAILABLE', // Special error code for UI to handle
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to create Stripe Connect account',
      };
    }
  }

  /**
   * PayPal: off-platform direct link (seller's PayPal.me / payment URL).
   * BookAPT does not process these payments.
   */
  async checkPayPalStatus(businessId: string): Promise<PaymentProviderStatus> {
    try {
      const { getSavedPayPalDirectUrl } = await import('./paypalDirectPaymentService');
      const { isPayPalDirectPaymentUrl } = await import('../utils/paypalDirectPayment');
      const url = await getSavedPayPalDirectUrl(businessId);
      const ready = !!url && isPayPalDirectPaymentUrl(url);
      return {
        connected: ready,
        ready,
        accountId: ready ? url : undefined,
      };
    } catch (error: any) {
      return {
        connected: false,
        ready: false,
        error: error.message || 'Failed to check PayPal status',
      };
    }
  }

  /**
   * Create PayPal/Braintree Connect account link (OAuth flow)
   */
  async createPayPalConnectAccount(
    businessId: string,
    businessName: string,
    returnUrl: string = 'bookapt://paypal-connect-success',
    refreshUrl: string = 'bookapt://paypal-connect-refresh'
  ): Promise<ConnectAccountResponse> {
    try {
      const owner = await AuthManager.getSavedBusinessOwner();
      const merchantTerms = getMerchantTermsForApi(owner);
      if (!merchantTerms) {
        return {
          success: false,
          error: 'MERCHANT_TERMS_REQUIRED',
        };
      }

      const response = await fetch(`${this.API_BASE_URL}/braintree-connect/create-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId,
          businessName,
          returnUrl,
          refreshUrl,
          merchantTerms,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: data.success || false,
        connectUrl: data.connectUrl,
        accountId: data.accountId,
        error: data.error,
      };
    } catch (error: any) {
      console.error('Error creating PayPal Connect account:', error);
      
      // Check if it's a connection error (backend not available)
      if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_CONNECTION_REFUSED') || error.message?.includes('NetworkError')) {
        return {
          success: false,
          error: 'BACKEND_NOT_AVAILABLE', // Special error code for UI to handle
        };
      }
      
      return {
        success: false,
        error: error.message || 'Failed to create PayPal Connect account',
      };
    }
  }

  /**
   * Disconnect PayPal account
   */
  async disconnectPayPal(businessId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { clearPayPalDirectUrl } = await import('./paypalDirectPaymentService');
      return clearPayPalDirectUrl(businessId);
    } catch (error: any) {
      console.error('Error disconnecting PayPal:', error);
      return {
        success: false,
        error: error.message || 'Failed to disconnect PayPal account',
      };
    }
  }

  /** Remove direct Stripe link or disconnect Connect account. */
  async disconnectStripe(businessId: string): Promise<{ success: boolean; error?: string }> {
    const { getSavedStripeDirectUrl, clearStripeDirectUrl } = await import(
      './stripeDirectPaymentService'
    );
    const direct = await getSavedStripeDirectUrl(businessId);
    if (direct) {
      return clearStripeDirectUrl(businessId);
    }

    const usesStripeConnect =
      STRIPE_CONNECT_REQUIRES_SUPER_ADMIN_APPROVAL || STRIPE_CONNECT_FOR_DELIVERY_ENABLED;

    if (!usesStripeConnect) {
      return { success: true };
    }

    try {
      const owner = await AuthManager.getSavedBusinessOwner();
      if (owner?.id === businessId) {
        await AuthManager.saveBusinessOwner({
          ...owner,
          stripeConnectAccountId: '',
        });
      }
    } catch (localErr) {
      console.warn('paymentProviderService: could not clear local stripeConnectAccountId', localErr);
    }

    try {
      const response = await fetch(
        `${this.API_BASE_URL}/stripe-connect/disconnect/${businessId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        // Local unlink succeeded — allow reconnect even if remote endpoint is unavailable
        return { success: true };
      }

      const data = await response.json();
      return {
        success: data.success !== false,
        error: data.error,
      };
    } catch (error: any) {
      console.error('Error disconnecting Stripe:', error);
      return { success: true };
    }
  }
}

// Export singleton instance
const paymentProviderService = new PaymentProviderService();
export default paymentProviderService;

